// apps/web/src/components/files/FileManager.tsx
// File management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { File as PlatformFile } from '../../types/file';
import { apiClient } from '../../lib/apiClient';

export function FileManager() {
  const [files, setFiles] = useState<PlatformFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/files');
        setFiles(response.data.files);
      } catch (err) {
        setError('Failed to load files');
        console.error('Error loading files:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const handleUploadFile = async (fileData: any) => {
    try {
      const response = await apiClient.post('/api/v1/files', fileData);
      setFiles([response.data, ...files]);
      setShowUploadForm(false);
    } catch (err) {
      setError('Failed to upload file');
      console.error('Error uploading file:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading files...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="file-manager">
      <div className="header">
        <h2>File Management</h2>
        <button 
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="btn btn-primary"
        >
          {showUploadForm ? 'Cancel' : 'Upload File'}
        </button>
      </div>

      {showUploadForm && (
        <FileUploadForm 
          onSubmit={handleUploadFile} 
          onCancel={() => setShowUploadForm(false)} 
        />
      )}

      <div className="files-grid">
        {files.map(file => (
          <FileCard key={file.id} file={file} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function FileCard({ file }: { file: PlatformFile }) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'IMAGE': return '🖼️';
      case 'VIDEO': return '🎬';
      case 'DOCUMENT': return '📄';
      case 'AUDIO': return '🎵';
      case 'ARCHIVE': return '📦';
      default: return '📁';
    }
  };

  return (
    <div className="file-card">
      <div className="file-header">
        <span className="file-icon">{getTypeIcon(file.fileType)}</span>
        <h3>{file.name}</h3>
      </div>
      <p>{file.description || 'No description'}</p>
      <p><strong>Size:</strong> {(file.fileSize / 1024).toFixed(1)} KB</p>
      <p><strong>Type:</strong> {file.fileType}</p>
      <p><strong>Created:</strong> {new Date(file.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-outline">Download</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function FileUploadForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fileType: 'DOCUMENT',
    file: null as File | null,
    tags: [] as string[]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="file-upload-form">
      <h3>Upload New File</h3>
      <input
        type="text"
        placeholder="File Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.fileType}
        onChange={(e) => setFormData({...formData, fileType: e.target.value})}
      >
        <option value="IMAGE">Image</option>
        <option value="VIDEO">Video</option>
        <option value="DOCUMENT">Document</option>
        <option value="AUDIO">Audio</option>
        <option value="ARCHIVE">Archive</option>
        <option value="OTHER">Other</option>
      </select>
      <input
        type="file"
        onChange={(e) => setFormData({...formData, file: e.target.files?.[0] || null})}
        required
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Upload File</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}